import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Search, Users, X } from "lucide-react";

/*
 * Guest Check-In Kiosk — public, token-authenticated. Door coordinators open
 * this on a tablet/phone (QR-scanned from Command Mode) and tap each guest as
 * they arrive. Uses:
 *   GET  /api/checkin/:event_id/guests?t=TOKEN
 *   POST /api/checkin/:event_id/:guest_id?t=TOKEN
 * No login required.
 */

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuestCheckInKiosk() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    if (!token) return setError("Missing kiosk token.");
    try {
      const res = await axios.get(`${API}/api/checkin/${eventId}/guests`, { params: { t: token } });
      setData(res.data);
    } catch {
      setError("Kiosk unavailable. Ask the planner for a fresh link.");
    }
  }, [eventId, token]);

  useEffect(() => { load(); }, [load]);
  // Poll every 15s so multiple staffers stay in sync
  useEffect(() => {
    const i = setInterval(load, 15_000);
    return () => clearInterval(i);
  }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const lc = q.trim().toLowerCase();
    if (!lc) return data.guests;
    return data.guests.filter((g) =>
      (g.name || "").toLowerCase().includes(lc) ||
      (g.group_label || "").toLowerCase().includes(lc));
  }, [data, q]);

  const toggleCheckIn = async (guest) => {
    setSaving(guest.id);
    try {
      await axios.post(`${API}/api/checkin/${eventId}/${guest.id}`, {}, { params: { t: token } });
      await load();
    } catch {
      // ignore
    } finally {
      setSaving(null);
    }
  };

  if (error) {
    return (
      <div className="theme-light min-h-screen bg-[#F7F7F8] flex items-center justify-center p-6">
        <div className="bevel-lg border border-hair p-6 max-w-md">
          <div className="font-mono text-[10px] tracking-widest uppercase text-[#EF4444]">Kiosk</div>
          <div className="font-display text-xl font-bold text-[#0A0A0A] mt-1">Unavailable</div>
          <div className="text-sm text-[#52525B] mt-2">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="theme-light min-h-screen bg-[#F7F7F8] flex items-center justify-center">
        <div className="font-mono text-xs tracking-widest uppercase text-[#71717A]">Loading kiosk…</div>
      </div>
    );
  }

  const { event, totals } = data;

  return (
    <div className="theme-light min-h-screen bg-[#F7F7F8]" data-testid="guest-kiosk">
      <header className="bevel border border-hair mx-4 mt-4 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#71717A]">
            Door Check-In
          </div>
          <div className="font-display text-2xl font-black text-[#0A0A0A] leading-tight">
            {event.title}
          </div>
          {event.venue && (
            <div className="text-sm text-[#52525B]">{event.venue}</div>
          )}
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] tracking-widest uppercase text-[#71717A]">Arrived</div>
          <div className="font-display text-3xl font-black text-emerald-600 tabular-nums" data-testid="kiosk-count">
            {totals.checked_in}<span className="text-[#A1A1AA]">/{totals.total}</span>
          </div>
        </div>
      </header>

      <div className="mx-4 mt-4">
        <div className="relative">
          <Search size={14} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search guest name or party…"
            data-testid="kiosk-search"
            className="w-full h-12 pl-9 pr-3 rounded-lg bevel-inset border border-hair text-[#0A0A0A] text-base focus:outline-none focus:border-[#06B6D4]"
          />
        </div>
      </div>

      <ul className="mx-4 mt-3 mb-8 space-y-2">
        {filtered.length === 0 && (
          <li className="text-[#71717A] text-center py-10 font-mono text-xs uppercase tracking-widest">
            No matches
          </li>
        )}
        {filtered.map((g) => {
          const checked = !!g.checked_in_at;
          return (
            <li key={g.id}>
              <button
                type="button"
                onClick={() => toggleCheckIn(g)}
                disabled={saving === g.id}
                data-testid={`kiosk-guest-${g.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left ${
                  checked
                    ? "bevel-2 border-emerald-500/40 bg-emerald-50"
                    : "bevel border-hair"
                }`}
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                  checked ? "bg-emerald-500 text-white" : "bg-[#F4F4F5] text-[#71717A]"
                }`}>
                  {checked ? <CheckCircle2 size={22} strokeWidth={1.75} /> : <Users size={19} strokeWidth={1.75} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold ${checked ? "text-emerald-700" : "text-[#0A0A0A]"}`}>
                    {g.name}
                  </div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-[#71717A] truncate">
                    {[g.group_label, `party ${g.party_size}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {checked ? (
                  <span className="font-mono text-[10px] tracking-widest uppercase text-emerald-600">
                    Arrived
                  </span>
                ) : (
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#0A0A0A]">
                    Tap →
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
